import React from "react";
import {
  Box,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  DoneAll,
  DoneOutlined,
  ErrorOutlined,
  AccessTime,
  ReplyOutlined,
} from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { formatText } from "../../../helpers";
import { IWhatsAppActivity } from "../../../redux/activity/activity.types";
import { PreviewTemplate } from "../../../components/Preview/PreviewTemplate";
import { ReplyPreviewTemplate } from "../../../components/Preview/ReplyPreviewTemplate";
import { MessageActionsPopover } from "../components/MessageActionsPopover";
import { IWhatsAppTemplateData } from "../../../redux/whatsappTemplate/whatsappTemplate.types";
import { maskRestrictedMessage } from "../services/chat-contact.service";
import { useTimezoneFormatter } from "../../../hooks/useTimezoneFormatter";
import { InternalBubble } from "./components/InternalBubble";
import { MediaRenderer } from "./components/MediaRenderer";
import { parseMaybeJson } from "../../../helpers/parseMaybeJson";

interface IWhatsAppMessageRowProps {
  data: IWhatsAppActivity;
  currentPhone: string;
  whatsappNumber: string;
  scannedPhoneNumber: string;
  propUseQRSend: boolean;
  messagesList: IWhatsAppActivity[];
  onReplySelect: (message: IWhatsAppActivity) => void;
  onForwardMessage?: (data: IWhatsAppActivity) => void;
  onRestrictMessage?: (data: IWhatsAppActivity) => void;
  onShowRestrictedMessage?: (data: IWhatsAppActivity) => void;
  visibleRestrictedMessages?: Set<string>;
  isFromActivityRoot?: boolean;
}

export const WhatsAppMessageRow: React.FC<IWhatsAppMessageRowProps> = ({
  data,
  currentPhone,
  whatsappNumber,
  scannedPhoneNumber,
  propUseQRSend,
  messagesList,
  onReplySelect,
  onForwardMessage,
  onRestrictMessage,
  onShowRestrictedMessage,
  visibleRestrictedMessages = new Set(),
  isFromActivityRoot = false,
}) => {
  const theme = useTheme();
  const { ftDateTime } = useTimezoneFormatter();

  const [isHovered, setIsHovered] = React.useState(false);

  // ✅ Get replied message if reply_from_conversation_id exists
  const repliedMessage = React.useMemo(() => {
    if (!data?.reply_from_conversation_id || !messagesList?.length) return null;
    return messagesList.find(
      (msg) => msg.conversation_id === data.reply_from_conversation_id,
    );
  }, [data?.reply_from_conversation_id, messagesList]);

  const renderStatus = React.useMemo(() => {
    switch (data?.status?.toUpperCase()) {
      case "PENDING":
        return (
          <Tooltip title="Sending..." arrow>
            <AccessTime fontSize="small" sx={{ color: "grey.500" }} />
          </Tooltip>
        );
      case "INITIATE":
      case "SENT":
        return (
          <Tooltip title="Sent" arrow>
            <DoneOutlined fontSize="small" color="disabled" />
          </Tooltip>
        );
      case "DELIVERED":
        return (
          <Tooltip title="Delivered" arrow>
            <DoneAll fontSize="small" color="disabled" />
          </Tooltip>
        );
      case "READ":
        return (
          <Tooltip title="Seen" arrow>
            <DoneAll fontSize="small" color="secondary" />
          </Tooltip>
        );
      case "SERVICE_OFF":
        return (
          <Tooltip title="Service Off" arrow>
            <ErrorOutlined color="error" fontSize="small" />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title={formatText(data?.status || "")} arrow>
            <ErrorOutlined color="error" fontSize="small" />
          </Tooltip>
        );
    }
  }, [data?.status]);

  const isSender = scannedPhoneNumber
    ? scannedPhoneNumber !== data?.to_number
    : whatsappNumber !== data?.to_number;

  // Helper function to check if message is restricted
  const isMessageRestricted = (
    data: IWhatsAppActivity,
    contentItem?: any,
  ): boolean => {
    // Check message-level is_restricted (can be 1, true, or any truthy value)
    const messageLevelRestricted =
      data?.is_restricted === 1 ||
      data?.is_restricted === true ||
      Boolean(data?.is_restricted);

    // Check content-item-level is_restricted
    const contentLevelRestricted =
      contentItem?.is_restricted === 1 ||
      contentItem?.is_restricted === true ||
      Boolean(contentItem?.is_restricted);

    return messageLevelRestricted || contentLevelRestricted;
  };

  const getTemplateData = (data: IWhatsAppActivity): IWhatsAppTemplateData => {
    if (!data || !data.content) {
      return {
        name: "",
        category: "",
        example: { header_text: [], body_text: [] },
        components: [],
        header: {
          type: "HEADER",
          format: "TEXT",
          text: null,
          example: { header_text: [], header_handle: null },
        },
        body: { type: "BODY", text: "", example: { body_text: [] } },
        buttons: { type: "BUTTONS", buttons: [] },
        footer: { type: "FOOTER", text: "" },
        status: null,
        rejected_reason: null,
        company_outlet_uuid: "",
        attachment: [],
        content: [],
        meta_id: null,
      };
    }

    const baseTemplate: IWhatsAppTemplateData = {
      name: "",
      category: "",
      example: { header_text: [], body_text: [] },
      components: [],
      header: {
        type: "HEADER",
        format: "TEXT",
        text: null,
        example: { header_text: [], header_handle: null },
      },
      body: {
        type: "BODY",
        text: (() => {
          const firstContent = data?.content?.[0];
          const isRestricted = isMessageRestricted(data, firstContent);
          const text = firstContent?.text || "";
          const isVisible = visibleRestrictedMessages.has(data.conversation_id);

          if (isRestricted && text) {
            return isVisible ? text : maskRestrictedMessage(text);
          }
          return text;
        })(),
        example: { body_text: [] },
      },
      buttons: { type: "BUTTONS", buttons: [] },
      footer: { type: "FOOTER", text: "" },
      status: null,
      rejected_reason: null,
      company_outlet_uuid: "",
      attachment: data.attachments || [],
      content: Array.isArray(data.content)
        ? data.content.map((item) =>
          typeof item === "string" ? item : JSON.stringify(item),
        )
        : [],
      meta_id: null,
    };

    data.content?.forEach((contentItem) => {
      if (!contentItem) return;
      switch (contentItem.type) {
        case "HEADER":
          baseTemplate.header = {
            type: "HEADER",
            format: contentItem.format,
            text: contentItem.text || null,
            example: {
              header_text: [],
              header_handle:
                contentItem.example?.header_handle?.map((handle, index) => ({
                  key: `file-${index}`,
                  file: null,
                  path: handle,
                  url: handle,
                })) || null,
            },
          };
          break;
        case "BODY":
          // Check both message-level and content-item-level is_restricted
          const isRestricted = isMessageRestricted(data, contentItem);
          const isVisible = visibleRestrictedMessages.has(data.conversation_id);
          baseTemplate.body = {
            type: "BODY",
            text:
              isRestricted && contentItem.text
                ? isVisible
                  ? contentItem.text
                  : maskRestrictedMessage(contentItem.text)
                : contentItem.text || "",
            example: { body_text: [] },
          };
          break;
        case "FOOTER":
          baseTemplate.footer = {
            type: "FOOTER",
            text: contentItem.text,
          };
          break;
        case "BUTTONS":
          baseTemplate.buttons = {
            type: "BUTTONS",
            buttons:
              contentItem.buttons?.map((btn) => ({
                type: btn.type,
                text: btn.text,
              })) || [],
          };
          break;
      }
    });

    return baseTemplate;
  };

  const templateData = getTemplateData(data);

  // Parse media for rendering below message text
  const parsedMedia = data.media
    ? parseMaybeJson<Array<{ url: string; type?: string; caption?: string }>>(data.media as any)
    : null;

  // ✅ Scroll to replied message
  const handleReplyClick = React.useCallback(() => {
    if (!repliedMessage?.conversation_id) return;
    const targetEl = document.getElementById(
      `msg-${repliedMessage.conversation_id}`,
    );
    if (targetEl) {
      targetEl.scrollIntoView({ behavior: "smooth", block: "center" });
      // Optional: add a short highlight flash effect
      targetEl.classList.add("flash-highlight");
      setTimeout(() => {
        targetEl.classList.remove("flash-highlight");
      }, 1500);
    }
  }, [repliedMessage]);

  // ✅ Early return for AI internal messages (chose not to reply)
  if (data.ai_internal) {
    return (
      <InternalBubble
        messageContent={data.message_content || (data.content?.[0] as any)?.text || null}
        createdAt={data.create_ts || data.insert_ts}
      />
    );
  }

  return (
    <Box
      id={`msg-${data.conversation_id}`} // ✅ unique id for scroll target
      position="relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="group"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: isSender ? "flex-end" : "flex-start",
        mb: 1.5,
        px: 1,
        scrollMarginTop: "80px", // in case of sticky header
        position: "relative",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 0,
          flexDirection: isSender ? "row-reverse" : "row",
          width: "100%",
        }}
      >
        {/* Reply Icon on Hover - Next to message */}
        {/* <Box
          sx={{
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.2s ease",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: "50%",
            bgcolor: "rgba(0, 0, 0, 0.05)",
            order: isSender ? -1 : 1,
            marginLeft: isSender ? "-8px" : "-8px", // Pull closer for left messages
    marginRight: isSender ? "-8px" : "-8px",
            "&:hover": {
              bgcolor: "rgba(0, 0, 0, 0.1)",
            },
          }}
          onClick={(e) => {
            e.stopPropagation();
            onReplySelect(data);
          }}
        >
          <ReplyOutlined
            sx={{
              color: "#54656f",
              fontSize: 20,
              "&:hover": {
                color: "#008069",
              },
            }}
          />
        </Box> */}

        <Stack
          sx={{
            width: "fit-content",
            // maxWidth: { xs: "85%", sm: "65%" },
            maxWidth: { xs: "85%", sm: isFromActivityRoot ? "85%" : "65%" },
            position: "relative",
          }}
        >
          {/* Reply Icon on Hover - Positioned absolutely next to bubble */}
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              transform: "translateY(-50%)",
              [isSender ? "left" : "right"]: "-40px", // Left side for right messages, right side for left messages
              opacity: isHovered ? 1 : 0,
              transition: "opacity 0.2s ease",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 32,
              height: 32,
              borderRadius: "50%",
              bgcolor: "rgba(0, 0, 0, 0.05)",
              zIndex: 10,
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.1)",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              onReplySelect(data);
            }}
          >
            <ReplyOutlined
              sx={{
                color: "#54656f",
                fontSize: 20,
                "&:hover": {
                  color: "#008069",
                },
              }}
            />
          </Box>
          {/* ✅ Reply Preview UI */}
          {repliedMessage && (
            <>
              <Box position="relative">
                {/* 3-dots menu for message actions */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 1000,
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.2s ease",
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "50%",
                    boxShadow: theme.shadows[2],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MessageActionsPopover
                    message={data}
                    onReply={onReplySelect}
                    onForward={onForwardMessage}
                    onRestrict={onRestrictMessage}
                    onShowRestrictedMessage={onShowRestrictedMessage}
                    isRestrictedMessageVisible={visibleRestrictedMessages.has(
                      data.conversation_id,
                    )}
                    isSender={isSender}
                  />
                </Box>
                <Paper
                  elevation={0}
                  onClick={(e) => {
                    e.stopPropagation(); // prevent toggling view
                    handleReplyClick();
                  }}
                  sx={{
                    borderRadius: isSender
                      ? "7.5px 0px 7.5px 7.5px"
                      : "0px 7.5px 7.5px 7.5px",
                    bgcolor: isSender ? "#d9fdd3" : "#ffffff",
                    boxShadow: "0 1px 0.5px rgba(0, 0, 0, 0.13)",
                    cursor: "pointer",
                    position: "relative",
                    // maxWidth: { xs: "85%", sm: "65%" },
                    // minWidth: { xs: "200px", sm: "250px" },

                    maxWidth: {
                      xs: "85%",
                      sm: isFromActivityRoot ? "85%" : "65%",
                    },
                    minWidth: {
                      xs: "200px",
                      sm: isFromActivityRoot ? "200px" : "250px",
                    },

                    width: "fit-content",
                    marginLeft: isSender ? "auto" : "0",
                    marginRight: isSender ? "0" : "auto",
                  }}
                >
                  <Stack
                    sx={{
                      p: 1.5,
                      gap: 1,
                    }}
                  >
                    <Stack
                      direction={"column"}
                      spacing={0.5}
                      sx={{
                        p: 1,
                        borderLeft: `4px solid #008069`,
                        borderRadius: "4px",
                        bgcolor: isSender
                          ? "rgba(255, 255, 255, 0.15)"
                          : "rgba(0, 0, 0, 0.05)",
                        color: "rgba(0, 0, 0, 0.7)",
                        minWidth: 0,
                        mb: 1,
                        opacity: 0.8,
                        cursor: "pointer",
                        "&:hover": {
                          opacity: 1,
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReplyClick();
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: "#008069",
                          fontSize: "0.75rem",
                          mb: 0.5,
                        }}
                      >
                        Replied to message
                      </Typography>
                      <Box
                        sx={{
                          maxHeight: 40,
                          overflow: "hidden",
                          wordBreak: "break-word",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        <ReplyPreviewTemplate
                          template={getTemplateData(repliedMessage) as any}
                          headerType=""
                          headerVariableValue=""
                          bgImage={false}
                          createdByName={
                            repliedMessage?.created_by_name || "Replied message"
                          }
                        />
                      </Box>
                    </Stack>

                    <Box>
                      <PreviewTemplate
                        template={templateData as any}
                        headerType=""
                        headerVariableValue=""
                        bgImage={false}
                        renderInsideReply
                        isSender={isSender}
                        isFromActivityRoot={isFromActivityRoot}
                      />
                      {parsedMedia && Array.isArray(parsedMedia) && parsedMedia.length > 0 && (
                        <MediaRenderer media={parsedMedia} />
                      )}
                    </Box>
                  </Stack>
                </Paper>
              </Box>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  justifyContent: isSender ? "flex-end" : "flex-start",
                  mt: 0.5,
                  px: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6875rem",
                    color: "rgba(0, 0, 0)",
                    textAlign: isSender ? "right" : "left",
                    
                  }}
                >
                  {`(By - ${data?.created_by_name || ""}) ${ftDateTime(
                    data?.insert_ts,
                  )}`}
                  {/* {ftDateTime(data?.insert_ts)} */}
                </Typography>
                {isSender && (
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    {renderStatus}
                  </Stack>
                )}
              </Stack>
            </>
          )}

          {/* ✅ Actual message */}
          {!repliedMessage && (
            <>
              <Box position="relative">
                {/* 3-dots menu for message actions */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    zIndex: 1000,
                    opacity: isHovered ? 1 : 0,
                    transition: "opacity 0.2s ease",
                    backgroundColor: theme.palette.background.paper,
                    borderRadius: "50%",
                    boxShadow: theme.shadows[2],
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <MessageActionsPopover
                    message={data}
                    onReply={onReplySelect}
                    onForward={onForwardMessage}
                    onRestrict={onRestrictMessage}
                    onShowRestrictedMessage={onShowRestrictedMessage}
                    isRestrictedMessageVisible={visibleRestrictedMessages.has(
                      data.conversation_id,
                    )}
                    isSender={isSender}
                  />
                </Box>
                <PreviewTemplate
                  template={templateData as any}
                  headerType=""
                  headerVariableValue=""
                  bgImage={false}
                  isSender={isSender}
                />
                {parsedMedia && Array.isArray(parsedMedia) && parsedMedia.length > 0 && (
                  <MediaRenderer media={parsedMedia} />
                )}
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  justifyContent: isSender ? "flex-end" : "flex-start",
                  mt: 0.5,
                  px: 1,
                  width: "100%",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.6875rem",
                    color: "rgb(0, 0, 0)",
                    textAlign: isSender ? "right" : "left",
                  }}
                >
                  {`(By - ${data?.created_by_name || ""}) ${ftDateTime(
                    data?.insert_ts,
                  )}`}
                </Typography>
                {isSender && (
                  <Stack direction="row" alignItems="center" spacing={0.25}>
                    {renderStatus}
                  </Stack>
                )}
              </Stack>
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default WhatsAppMessageRow;
